// ── PRODUCT TOUR — spotlight coach marks ─────────────────────
// A stepped walkthrough: a dimmed scrim with a cut-out around the current
// target, plus a popover anchored to it. Step counter, Back/Next/Skip.
//
// NOTE ON INTENT: design/ONBOARDING_SPEC.md states "Onboarding is NOT a
// tour." This reverses that deliberately and at the owner's direction, with
// the agreement that it can fall back to the post-aha variant. That fallback
// is TOUR_MODE below — one constant, not a rewrite.
import { useState, useEffect, useCallback, useRef, useLayoutEffect } from "react";
import { TOKENS } from "../tokens";
import { FONT_H, FONT_UI, FONT_MONO, useT } from "../theme";

// "full"   — auto-launches for every new user on first login (GrantWizard-style)
// "hybrid" — never auto-launches at signup; offered only after the first
//            extraction renders, so the countdown-to-aha path stays clean
export const TOUR_MODE = "full";

const SEEN_KEY = "nofodesk_tour_v1";
const REDUCED  = typeof window !== "undefined" && window.matchMedia
  && window.matchMedia("(prefers-reduced-motion: reduce)").matches;

export function hasSeenTour() {
  try { return !!localStorage.getItem(SEEN_KEY); } catch { return true; }
}
export function markTourSeen(how) {
  try { localStorage.setItem(SEEN_KEY, how || "done"); } catch {}
}

// Steps are declarative. `target` is a [data-tour] value; a step with no
// target renders centred (used for the opening and closing beats).
// `view` navigates the app before the step so its target exists.
export const TOUR_STEPS = [
  {
    id: "welcome", target: null,
    title: "Welcome to NofoDesk",
    body: "A quick look at how this works — about 40 seconds. You can skip at any point and replay it later from the sidebar checklist.",
  },
  {
    id: "input", target: "chat-input", view: "extract", placement: "top",
    title: "Start with a grant document",
    body: "Paste a grant URL or drop in a PDF. NofoDesk reads the whole document and pulls out every field — deadline, award range, eligibility, required attachments — in about thirty seconds.",
  },
  {
    id: "attach", target: "attach-file", view: "extract", placement: "top",
    title: "PDF, Word or Excel",
    body: "Scanned documents work too. If a PDF has no text layer, it gets run through OCR rather than failing.",
  },
  {
    id: "sample", target: "sample-button", view: "extract", placement: "bottom",
    title: "Or try it on a sample first",
    body: "Runs on a real public NOFO so you can see the output before spending anything. It does not count against your monthly extractions.",
  },
  {
    id: "propose", target: "mode-propose", view: "extract", placement: "top",
    title: "Draft the proposal too",
    body: "Switch to Develop Proposal and NofoDesk drafts sections grounded in the funder's own language and your organisation profile.",
  },
  {
    id: "discover", target: "nav-discover", view: "discover", placement: "right",
    title: "Find grants you haven't seen",
    body: "A running feed of open calls from global funders, refreshed on a schedule. Filter by sector and deadline, then extract any of them in one click.",
  },
  {
    id: "tracker", target: "nav-grantOS", placement: "right",
    title: "Track everything you're working on",
    body: "Your pipeline across its whole lifecycle — active, submitted, completed. Deadlines surface here and in the calendar, with email alerts before each one.",
  },
  {
    id: "settings", target: "nav-settings", placement: "right",
    title: "Set up your organisation once",
    body: "Fill in your org profile and NofoDesk can check you against each grant's eligibility criteria automatically, instead of you reading them line by line.",
  },
  {
    id: "done", target: null,
    title: "That's the tour",
    body: "Paste a grant URL to get started — or run the sample if you'd rather see it work first. You can replay it any time from “Replay the tour” in the sidebar checklist.",
  },
];

const GAP = 14;          // space between spotlight and popover
const PAD = 8;           // spotlight padding around the target
const POPOVER_W = 340;

export default function ProductTour({ steps = TOUR_STEPS, onNavigate, onClose, onFinish }) {
  const T = useT();
  const [i, setI]           = useState(0);
  const [rect, setRect]     = useState(null);   // target rect, or null = centred
  const [popH, setPopH]     = useState(210);
  const popRef              = useRef(null);
  const step                = steps[i];
  const isLast              = i === steps.length - 1;

  const finish = useCallback((how) => {
    markTourSeen(how);
    if (how === "done" && onFinish) onFinish();
    onClose?.(how);
  }, [onClose, onFinish]);

  // Navigate the app so this step's target is mounted, then measure it.
  useEffect(() => {
    if (step?.view && onNavigate) onNavigate(step.view);
  }, [i, step, onNavigate]);

  const measure = useCallback(() => {
    if (!step?.target) { setRect(null); return; }
    const el = document.querySelector(`[data-tour="${step.target}"]`);
    if (!el) { setRect(null); return; }   // missing target → centre, never break
    const r = el.getBoundingClientRect();
    if (r.width === 0 && r.height === 0) { setRect(null); return; }
    setRect({ top: r.top, left: r.left, width: r.width, height: r.height });
  }, [step]);

  // Targets mount asynchronously after a view change, so poll briefly rather
  // than measuring once and giving up.
  useEffect(() => {
    measure();
    const t1 = setTimeout(measure, 60);
    const t2 = setTimeout(measure, 220);
    const t3 = setTimeout(measure, 500);
    return () => { clearTimeout(t1); clearTimeout(t2); clearTimeout(t3); };
  }, [measure]);

  useEffect(() => {
    const onScrollOrResize = () => measure();
    window.addEventListener("resize", onScrollOrResize);
    window.addEventListener("scroll", onScrollOrResize, true);
    return () => {
      window.removeEventListener("resize", onScrollOrResize);
      window.removeEventListener("scroll", onScrollOrResize, true);
    };
  }, [measure]);

  // Bring the target into view if it is off screen.
  useEffect(() => {
    if (!step?.target) return;
    const el = document.querySelector(`[data-tour="${step.target}"]`);
    if (!el) return;
    const r = el.getBoundingClientRect();
    const off = r.top < 70 || r.bottom > window.innerHeight - 70;
    if (off) el.scrollIntoView({ behavior: REDUCED ? "auto" : "smooth", block: "center" });
  }, [i, step]);

  useLayoutEffect(() => {
    if (popRef.current) setPopH(popRef.current.offsetHeight || 210);
  }, [i, rect]);

  // Keyboard: Esc skips, arrows and Enter move.
  useEffect(() => {
    const onKey = (e) => {
      if (e.key === "Escape")     { e.preventDefault(); finish("skipped"); }
      else if (e.key === "ArrowRight" || e.key === "Enter") { e.preventDefault(); isLast ? finish("done") : setI(n => n + 1); }
      else if (e.key === "ArrowLeft") { e.preventDefault(); setI(n => Math.max(0, n - 1)); }
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [isLast, finish]);

  if (!step) return null;

  const vw = window.innerWidth, vh = window.innerHeight;
  const isMobile = vw < 768;
  const popW = isMobile ? Math.min(vw - 24, POPOVER_W) : POPOVER_W;

  // ── Placement ───────────────────────────────────────────────
  // Preference order: the step's own hint, then whichever side has room.
  let popStyle;
  if (!rect) {
    popStyle = { top: Math.max(16, vh / 2 - popH / 2), left: Math.max(12, vw / 2 - popW / 2) };
  } else {
    const spaceBelow = vh - (rect.top + rect.height), spaceAbove = rect.top;
    const spaceRight = vw - (rect.left + rect.width);
    let place = step.placement || "bottom";
    if (isMobile && (place === "right" || place === "left")) place = spaceBelow > popH + GAP ? "bottom" : "top";
    if (place === "bottom" && spaceBelow < popH + GAP && spaceAbove > popH + GAP) place = "top";
    if (place === "top"    && spaceAbove < popH + GAP && spaceBelow > popH + GAP) place = "bottom";
    if (place === "right"  && spaceRight < popW + GAP) place = spaceBelow > popH + GAP ? "bottom" : "top";

    let top, left;
    if (place === "bottom")      { top = rect.top + rect.height + GAP; left = rect.left + rect.width / 2 - popW / 2; }
    else if (place === "top")    { top = rect.top - popH - GAP;        left = rect.left + rect.width / 2 - popW / 2; }
    else                         { top = rect.top + rect.height / 2 - popH / 2; left = rect.left + rect.width + GAP; }

    // Clamp inside the viewport so a popover never hangs off an edge.
    top  = Math.min(Math.max(12, top), Math.max(12, vh - popH - 12));
    left = Math.min(Math.max(12, left), Math.max(12, vw - popW - 12));
    popStyle = { top, left };
  }

  const dim = "rgba(4,8,18,0.68)";
  const btnBase = { borderRadius: 8, fontFamily: FONT_UI, fontSize: 13, fontWeight: 600, cursor: "pointer", padding: "8px 16px", minHeight: 36, border: "1px solid transparent" };

  return (
    <div style={{ position: "fixed", inset: 0, zIndex: 10000 }} role="dialog" aria-modal="true" aria-labelledby="nd-tour-title">
      {/* Scrim + cut-out. A single element with a very large spread shadow
          dims everything except its own box — no SVG mask, and the hole
          keeps the target's rounded shape. */}
      {rect ? (
        <div style={{
          position: "fixed",
          top: rect.top - PAD, left: rect.left - PAD,
          width: rect.width + PAD * 2, height: rect.height + PAD * 2,
          borderRadius: 10,
          boxShadow: `0 0 0 9999px ${dim}`,
          border: `2px solid ${TOKENS.gold}`,
          pointerEvents: "none",
          transition: REDUCED ? "none" : "top .3s cubic-bezier(.22,.61,.36,1), left .3s cubic-bezier(.22,.61,.36,1), width .3s, height .3s",
        }} />
      ) : (
        <div style={{ position: "fixed", inset: 0, background: dim }} />
      )}

      {/* Click-off-to-skip layer, behind the popover */}
      <div onClick={() => finish("skipped")} style={{ position: "fixed", inset: 0, cursor: "default" }} />

      <div ref={popRef} style={{
        position: "fixed", ...popStyle, width: popW,
        background: T.bgElevated, border: `1px solid ${T.border}`, borderRadius: 14,
        boxShadow: "0 18px 50px -12px rgba(0,0,0,0.55)",
        padding: "18px 20px 16px", boxSizing: "border-box",
      }}>
        <div style={{ fontFamily: FONT_MONO, fontSize: 10.5, letterSpacing: "0.12em", textTransform: "uppercase", color: T.textMuted, marginBottom: 7 }}>
          Step {i + 1} of {steps.length}
        </div>
        <div id="nd-tour-title" style={{ fontFamily: FONT_H, fontSize: 17, fontWeight: 700, color: T.text, marginBottom: 7, lineHeight: 1.3 }}>
          {step.title}
        </div>
        <p style={{ fontFamily: FONT_UI, fontSize: 13, color: T.textSub || T.textMuted, lineHeight: 1.62, margin: "0 0 16px" }}>
          {step.body}
        </p>

        <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", gap: 10, flexWrap: "wrap" }}>
          <button onClick={() => finish("skipped")}
            style={{ ...btnBase, background: "none", color: T.textMuted, padding: "8px 2px" }}>
            Skip tour
          </button>

          <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
            {/* Progress dots double as direct navigation */}
            <div style={{ display: "flex", gap: 5, marginRight: 4 }}>
              {steps.map((s, n) => (
                <button key={s.id} onClick={() => setI(n)} aria-label={`Go to step ${n + 1}`}
                  style={{ width: n === i ? 16 : 6, height: 6, borderRadius: 3, padding: 0, border: "none", cursor: "pointer",
                           background: n === i ? TOKENS.gold : n < i ? T.textMuted : T.border,
                           transition: REDUCED ? "none" : "width .22s ease, background .22s ease" }} />
              ))}
            </div>
            {i > 0 && (
              <button onClick={() => setI(n => n - 1)}
                style={{ ...btnBase, background: T.bgApp, color: T.textBody, border: `1px solid ${T.border}` }}>
                Back
              </button>
            )}
            <button onClick={() => (isLast ? finish("done") : setI(n => n + 1))} autoFocus
              style={{ ...btnBase, background: TOKENS.gold, color: "#fff" }}>
              {isLast ? "Get started" : "Next"}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}
